import React from 'react'
import { useIntl } from 'react-intl'
import { css } from '@emotion/css'
import moment from 'moment'
import { useOpenClosedVulns } from './api/use-open-closed-vulns'
import { VrModuleChartLayout, VrModuleChartLayoutHeader } from '../layout'
import { VrStandaloneModulePanel } from '../layout/vr-module-containers'
import { VrDateFilterDropdown } from '../../vr-date-filter-dropdown'
import { useVrDateFilterManager } from '../../use-vr-date-filter-manager'
import {
  VulnsOpenClosedChart,
  getTimelineConfigByDays
} from '../../../vulnerabilities-chart'
import { SelectedValueReportingFacet } from '../../../../graphql/schema/types/value-reporting'
import {
  useVrApiExportDialog,
  VrApiExportDialog
} from '../../export/vr-api-export-dialog'
import { getOpenClosedVulnsCurlCommand } from '../../export/vr-curl-commands'

export const VrModuleOpenClosedVulns = ({
  apiKey,
  authHeader,
  hostUrl,
  organizationUuid,
  selectedFacets
}: {
  apiKey: string
  authHeader: string
  hostUrl: string
  organizationUuid: string
  selectedFacets: SelectedValueReportingFacet[]
}) => {
  const intl = useIntl()
  const { openDialog, dialogProps } = useVrApiExportDialog()
  const dateRangeProps = useVrDateFilterManager()
  const selectedDayRange = moment(
    dateRangeProps.selectedDateRange?.endDate
  ).diff(moment(dateRangeProps.selectedDateRange?.startDate), 'days')

  const { resolution, stride } = getTimelineConfigByDays(selectedDayRange)
  const since = dateRangeProps.selectedDateRange?.startDate?.getTime()
  const until = dateRangeProps.selectedDateRange?.endDate?.getTime()

  const { isLoading, error, data } = useOpenClosedVulns({
    organizationUuid,
    resolution,
    since,
    until,
    facets: selectedFacets
  })
  const hasNoResults = !data?.series?.length && !isLoading

  return (
    <>
      <VrStandaloneModulePanel
        className={css`
          overflow: visible;
        `}
        testId="value-reporting-open-closed-vulns-module"
        onExportClick={isLoading ? undefined : openDialog}
        hasBorder={true}
        flex="1"
        order={3}
        minHeight={350}
      >
        <VrModuleChartLayout
          isLoading={isLoading}
          isError={error}
          header={
            <VrModuleChartLayoutHeader
              isError={error}
              toolbar={<VrDateFilterDropdown {...dateRangeProps} />}
              title={intl.formatMessage({
                id: 'title_valueReporting_openClosedVulnerabilities'
              })}
            />
          }
          chart={
            hasNoResults ? (
              <div
                className={css`
                  display: flex;
                  flex: 1;
                  align-items: center;
                  justify-content: center;
                `}
              >
                <div
                  className={css`
                    width: 50%;
                    text-align: center;
                  `}
                >
                  {intl.formatMessage(
                    { id: 'title_valueReporting_vulnRateNoResults' },
                    { days: selectedDayRange }
                  )}
                </div>
              </div>
            ) : (
              <VulnsOpenClosedChart
                height={224}
                stride={stride}
                resolution={resolution}
                timeline={data?.series}
              />
            )
          }
        />
      </VrStandaloneModulePanel>
      <VrApiExportDialog
        {...dialogProps}
        e2eTestId="value-reporting-open-closed-vulns-api-dialog"
        titleId="title_valueReporting_vulnRateApi"
        sections={[
          {
            curlCommand: getOpenClosedVulnsCurlCommand({
              apiKey,
              authHeader,
              organizationUuid,
              protocolAndHost: hostUrl,
              facets: selectedFacets,
              queryParameters: { resolution, since, until }
            })
          }
        ]}
      />
    </>
  )
}
